import { APP_FONTS, APP_TEXT_SIZES, type AppFont, type TypographySettings } from '@sahab/contracts'
import { jsonPreference } from './preferences'

/**
 * The company's typeface and text size (Settings › Fonts and text, the `typography` setting group).
 * Applied as `data-font` and `data-text-size` on <html>, which tokens.css turns into `--app-font` and
 * `--text-scale`. The design system's own face (IBM Plex) is bundled; any other face's files are
 * fetched only once a company picks it. Paper (document sheets, the print proof) keeps IBM Plex at
 * 14px whatever is chosen here.
 */

export const DEFAULT_TYPOGRAPHY: TypographySettings = { font: 'ibmPlex', textSize: 'default' }

const isTypography = (value: unknown): value is TypographySettings => {
  if (typeof value !== 'object' || value === null) return false
  const { font, textSize } = value as Record<string, unknown>
  return APP_FONTS.some((f) => f === font) && APP_TEXT_SIZES.some((s) => s === textSize)
}

/** The last value seen on this browser, so the next visit paints in the company's face at once. */
const cachedTypography = jsonPreference<TypographySettings>('typography', DEFAULT_TYPOGRAPHY, isTypography)

/** Each face's stylesheets (self-hosted, @fontsource): 400, 500 and 600/700 for the weights the UI uses. */
const FONT_LOADERS: Record<Exclude<AppFont, 'ibmPlex'>, () => Promise<unknown>> = {
  tajawal: () =>
    Promise.all([
      import('@fontsource/tajawal/400.css'),
      import('@fontsource/tajawal/500.css'),
      import('@fontsource/tajawal/700.css'),
    ]),
  cairo: () => import('@fontsource-variable/cairo/index.css'),
  almarai: () => Promise.all([import('@fontsource/almarai/400.css'), import('@fontsource/almarai/700.css')]),
  notoSans: () =>
    Promise.all([
      import('@fontsource-variable/noto-sans/index.css'),
      import('@fontsource-variable/noto-sans-arabic/index.css'),
    ]),
  readexPro: () => import('@fontsource-variable/readex-pro/index.css'),
}

const loaded = new Set<AppFont>()

/** Adds a face's @font-face rules once; the browser then downloads only the subsets a page uses. */
export function loadFont(font: AppFont): Promise<unknown> {
  if (font === 'ibmPlex' || loaded.has(font)) return Promise.resolve()
  loaded.add(font)
  return FONT_LOADERS[font]().catch(() => {
    // A failed chunk falls back to the next face in --app-font; a later call may try again.
    loaded.delete(font)
  })
}

/** Puts a typography value on <html> and remembers it for the next visit. */
export function applyTypography(value: TypographySettings): void {
  const root = document.documentElement
  root.dataset.font = value.font
  root.dataset.textSize = value.textSize
  void loadFont(value.font)
  cachedTypography.set(value)
}

/** At start-up, before the session is known: the company's choice as last seen on this browser. */
export function startTypography(): void {
  applyTypography(cachedTypography.get())
}
